"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import type { TrackingDataLayerItem } from "@/lib/tracking";
import { analyticsEnabled } from "@/lib/analytics-preferences";

declare global {
  interface Window {
    dataLayer?: TrackingDataLayerItem[];
    gtag?: (...args: unknown[]) => void;
    /** HubSpot tracking command queue; safe to push before the script loads. */
    _hsq?: unknown[][];
  }
}

/** Sends one GA4 and HubSpot page view for each App Router navigation.
 * The first load is skipped: gtag and the HubSpot script each record it. */
export function PageViewTracker() {
  const pathname = usePathname();
  const lastPage = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;

    const pagePath = `${pathname}${window.location.search}`;
    if (lastPage.current === null) {
      lastPage.current = pagePath;
      return;
    }
    if (lastPage.current === pagePath) return;
    lastPage.current = pagePath;
    if (!analyticsEnabled()) return;

    const pageView = {
      event: "dbm_page_view",
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    };

    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push(pageView);
    window.gtag?.("event", "page_view", {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
    const hsq = (window._hsq = window._hsq ?? []);
    hsq.push(["setPath", pagePath]);
    hsq.push(["trackPageView"]);
  }, [pathname]);

  return null;
}
